import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DateTime } from "luxon";
import { LinearProgress, useGetList, useGetRecordId } from "react-admin";
import {
  Box,
  Card,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { DashboardCard } from "../../../misc/DashboardCard";
import { useRecharts } from "../../../hooks/useRecharts";
import { useThemeColor } from "../../../hooks/useThemeColor";
import { Row } from "../../../misc/Row";
import { Column } from "../../../misc/Column";
import { serverAPI, StudentsSignupsStatsDto } from "../../../api/server.api";
import { DrivingLicenseClass } from "../../../model/DrivingLicenseClass";
import { Branch } from "../../../providers/branchesProvider";

type ViewType = "day" | "month" | "drivingLicenseClass-month" | "drivingLicenseClass-year";

export function StudentsSignupsStatsCard() {
  const [viewType, setViewType] = useState<ViewType>("day");
  const [fromDate, setFromDate] = useState(DateTime.now().setLocale("de").startOf("month"));
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);
  const [signupsStats, setSignupsStats] = useState<{
    results: Record<string, number>;
    trend: "up" | "down" | "stable";
    total: number;
  } | null>(null);
  const drivingSchoolId = useGetRecordId() as string;
  const isLoading = !signupsStats;
  const currentSelectionRef = useRef("");
  const drivingLicenseClassesRef = useRef<Array<DrivingLicenseClass> | undefined>();

  const { data: activeBranches = [] } = useGetList<Branch>("branches", {
    filter: { drivingSchoolId, isActive: true },
    pagination: { page: 1, perPage: 1000 },
  });

  const isYearPeriod = useMemo(() => viewType === "month" || viewType === "drivingLicenseClass-year", [viewType]);

  const fetchSignupsStats = useCallback(async () => {
    setSignupsStats(null);
    const from = fromDate.toISODate();
    const to = fromDate.endOf(isYearPeriod ? "year" : "month").toISODate();
    const currentSelection = `${drivingSchoolId}:${viewType}:${from}:${selectedBranchId ?? "all"}`;
    try {
      currentSelectionRef.current = currentSelection;
      const data = await serverAPI.getStudentsSignupsStats({
        drivingSchoolId,
        from,
        to,
        groupBy: viewType.includes("drivingLicenseClass") ? "drivingLicenseClass" : (viewType as "day" | "month"),
        branchId: selectedBranchId ?? undefined,
      });
      // Prevent that we show data for an outdated selection ...
      if (currentSelectionRef.current === currentSelection) {
        setSignupsStats(data);
      }
      if (viewType.includes("drivingLicenseClass")) {
        // Store driving license classes, so we can display them when fetching stats for another month ...
        drivingLicenseClassesRef.current = Object.keys(data.results) as Array<DrivingLicenseClass>;
      }
    } catch (error) {
      // Ignore errors for outdated selections ...
      if (currentSelectionRef.current === currentSelection) {
        setSignupsStats(null);
        console.error("Error fetching students signups stats", error);
      }
    }
  }, [drivingSchoolId, fromDate, viewType, selectedBranchId]);

  useEffect(() => {
    void fetchSignupsStats();
  }, [fetchSignupsStats]);

  const updateFromDate = (nextViewType: ViewType) => {
    setFromDate((prevState) =>
      nextViewType === "day" || nextViewType === "drivingLicenseClass-month"
        ? prevState.startOf("month")
        : prevState.startOf("year"),
    );
  };

  const goToPeriod = (delta: number) => {
    const unit = isYearPeriod ? "years" : "months";
    setFromDate((prevState) => prevState.plus({ [unit]: delta }));
  };

  return (
    <DashboardCard title="Reports">
      <Stack spacing={1}>
        <FormControl fullWidth size="small">
          <Select
            value={viewType}
            onChange={(event) => {
              const nextViewType = event.target.value as ViewType;
              setViewType(nextViewType);
              updateFromDate(nextViewType);
            }}
            disabled={isLoading}
          >
            <MenuItem value="day">Anmeldungen pro Tag</MenuItem>
            <MenuItem value="month">Anmeldungen pro Monat</MenuItem>
            <MenuItem value="drivingLicenseClass-month">Anmeldungen pro Klasse (Monat)</MenuItem>
            <MenuItem value="drivingLicenseClass-year">Anmeldungen pro Klasse (Jahr)</MenuItem>
          </Select>
        </FormControl>
        {activeBranches.length > 1 && (
          <FormControl fullWidth size="small">
            <Select
              value={selectedBranchId ?? "all"}
              onChange={(event) => setSelectedBranchId(event.target.value === "all" ? null : event.target.value)}
              disabled={isLoading}
              aria-label="Filiale"
            >
              <MenuItem value="all">Alle Filialen</MenuItem>
              {activeBranches.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                  {branch.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Stack>
      <Row justifyContent="space-between" alignItems="center">
        <Row>
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              fontSize: 15,
            }}
          >
            {fromDate.toFormat(`${isYearPeriod ? "" : "MMMM "}yyyy`)}
          </Typography>
        </Row>
        <Row>
          <IconButton
            size="small"
            onClick={() => goToPeriod(-1)}
            title="Vorherige Periode"
            aria-label="Vorherige Periode"
            disabled={isLoading}
          >
            <NavigateBeforeIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => goToPeriod(1)}
            title="Nächste Periode"
            aria-label="Nächste Periode"
            disabled={isLoading}
          >
            <NavigateNextIcon />
          </IconButton>
        </Row>
      </Row>
      {viewType === "day" ? (
        <_ByDayChart selectedMonth={fromDate} signupsStats={signupsStats} />
      ) : viewType === "month" ? (
        <_ByMonthChart selectedYear={fromDate.startOf("year")} signupsStats={signupsStats} />
      ) : viewType.includes("drivingLicenseClass") ? (
        <_ByClassChart signupsStats={signupsStats} drivingLicenseClasses={drivingLicenseClassesRef.current} />
      ) : null}
      <_SummaryBox
        localizedPeriod={isYearPeriod ? fromDate.toFormat("yyyy") : fromDate.toFormat("MMMM yyyy")}
        renderTopGap={viewType !== "month"}
        signupsStats={signupsStats}
      />
    </DashboardCard>
  );
}

function _calculateDomainMax(values: number[], defaultMax: number): number {
  if (values.length === 0) {
    return defaultMax;
  }

  const maxValue = Math.max(...values);
  return maxValue === 0 ? defaultMax : Math.ceil(maxValue / defaultMax) * defaultMax;
}

function _SignupsTooltip({ active, payload }: any) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  const pointData = payload[0].payload;
  return (
    <Card sx={{ padding: "8px", borderRadius: "8px", boxShadow: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
        {pointData.localizedDate}
      </Typography>
      <Typography variant="body2">
        {pointData.signups} {pointData.signups === 1 ? "Anmeldung" : "Anmeldungen"}
      </Typography>
    </Card>
  );
}

function _ByDayChart(props: { selectedMonth: DateTime; signupsStats: StudentsSignupsStatsDto | null }) {
  const { selectedMonth, signupsStats } = props;
  const { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } = useRecharts();
  const primaryColor = useThemeColor("primary");
  const theme = useTheme();

  const chartData = useMemo(() => {
    const daysInMonth = selectedMonth.daysInMonth;
    const year = selectedMonth.year;
    const data = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dayKey = day.toString().padStart(2, "0");
      const signups = signupsStats?.results[dayKey] ?? 0;
      data.push({
        day: dayKey,
        signups,
        localizedDate: `${day}.${selectedMonth.month}.${year}`,
      });
    }
    return data;
  }, [selectedMonth, signupsStats]);

  if (!(BarChart && Bar && XAxis && YAxis && ResponsiveContainer && Tooltip)) {
    return null;
  }

  const domainMax = _calculateDomainMax(
    chartData.map((item) => item.signups),
    10,
  );
  return (
    <Box sx={{ width: "100%", height: "150px", mt: 1 }}>
      <ResponsiveContainer height="100%">
        <BarChart data={chartData} margin={{ top: 0, right: 0, left: -5, bottom: -20 }}>
          <XAxis dataKey="dateLabel" tick={false} tickLine={false} axisLine={false} interval={0} />
          <YAxis
            tick={{ fontSize: 12, fill: theme.palette.text.secondary, dy: -4 }}
            tickLine={false}
            axisLine={false}
            domain={[0, domainMax]}
            ticks={[0, Math.ceil(domainMax / 2), domainMax]}
            width={30}
          />
          <Tooltip cursor={{ opacity: 0.1 }} content={_SignupsTooltip} />
          <Bar
            dataKey="signups"
            shape={(shapeProps: any) => _CustomVerticalBarShape(shapeProps, theme.palette.divider)}
            fill={primaryColor.main}
          />
        </BarChart>
      </ResponsiveContainer>
      <_CustomChartBase
        leftLabel={selectedMonth.startOf("month").toFormat("d. MMMM")}
        rightLabel={selectedMonth.endOf("month").toFormat("dd. MMMM")}
      />
    </Box>
  );
}

function _ByMonthChart(props: { selectedYear: DateTime; signupsStats: StudentsSignupsStatsDto | null }) {
  const { selectedYear, signupsStats } = props;
  const { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } = useRecharts();
  const primaryColor = useThemeColor("primary");
  const theme = useTheme();

  const chartData = useMemo(() => {
    const monthNames = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];
    const data = [];
    for (let month = 1; month <= 12; month++) {
      const monthKey = month.toString().padStart(2, "0");
      const signups = signupsStats?.results[monthKey] ?? 0;
      data.push({
        month: monthKey,
        signups,
        monthLabel: monthNames[month - 1],
        localizedDate: DateTime.fromObject({ year: selectedYear.year, month }).setLocale("de").toFormat("MMMM yyyy"),
      });
    }
    return data;
  }, [selectedYear, signupsStats]);

  if (!(BarChart && Bar && XAxis && YAxis && ResponsiveContainer && Tooltip)) {
    return null;
  }

  const domainMax = _calculateDomainMax(
    chartData.map((item) => item.signups),
    50,
  );
  return (
    <Box sx={{ width: "100%", height: "150px", mt: 1 }}>
      <ResponsiveContainer height="100%">
        <BarChart data={chartData} margin={{ top: 0, right: 0, left: -5, bottom: -5 }} barCategoryGap="20%">
          <XAxis
            dataKey="monthLabel"
            tick={{ fontSize: 12, fill: theme.palette.text.secondary, dy: 4 }}
            tickLine={false}
            axisLine={{ stroke: theme.palette.divider, strokeWidth: 1 }}
            interval={0}
          />
          <YAxis
            tick={{ fontSize: 12, fill: theme.palette.text.secondary, dy: -4 }}
            tickLine={false}
            axisLine={false}
            domain={[-domainMax * 0.08, domainMax]}
            ticks={[0, Math.ceil(domainMax / 2), domainMax]}
            width={30}
          />
          <Tooltip cursor={{ opacity: 0.1 }} content={_SignupsTooltip} />
          <Bar
            dataKey="signups"
            shape={(shapeProps: any) => _CustomVerticalBarShapeWide(shapeProps, theme.palette.divider)}
            fill={primaryColor.main}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

function _ByClassChart(props: {
  drivingLicenseClasses: Array<DrivingLicenseClass> | undefined;
  signupsStats: StudentsSignupsStatsDto | null;
}) {
  const { drivingLicenseClasses, signupsStats } = props;
  const { BarChart, Bar, XAxis, YAxis, ResponsiveContainer } = useRecharts();
  const primaryColor = useThemeColor("primary");
  const theme = useTheme();

  const chartData = useMemo(() => {
    return Object.entries(
      signupsStats?.results ?? Object.fromEntries((drivingLicenseClasses ?? []).map((it) => [it, 0])),
    ).map(([drivingLicenseClass, signups]) => ({
      drivingLicenseClass,
      signups,
    }));
  }, [signupsStats, drivingLicenseClasses]);

  if (!(BarChart && Bar && XAxis && YAxis && ResponsiveContainer)) {
    return null;
  }

  const domainMax = _calculateDomainMax(
    chartData.map((item) => item.signups),
    10,
  );

  return (
    <Box sx={{ width: "100%", height: "200px", mt: 1 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <XAxis type="number" tick={false} tickLine={false} axisLine={false} hide />
          <YAxis
            type="category"
            dataKey="drivingLicenseClass"
            tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
            tickLine={false}
            axisLine={false}
            width={14}
            interval={0}
          />
          <Bar
            dataKey="signups"
            shape={(shapeProps: any) => _CustomHorizontalBarShape(shapeProps, theme.palette.divider)}
            fill={primaryColor.main}
            label={({ x, y, width, height, value }) => {
              if (value === 0) {
                return <></>;
              }
              return (
                <text
                  x={x + width + 4}
                  y={y + height / 2}
                  fill={theme.palette.text.secondary}
                  fontSize={12}
                  fontWeight="bold"
                  textAnchor="start"
                  dominantBaseline="middle"
                >
                  {value}
                </text>
              );
            }}
          />
        </BarChart>
      </ResponsiveContainer>
      <_CustomChartBase leftLabel="0" rightLabel={domainMax > 0 ? domainMax.toString() : ""} />
    </Box>
  );
}

function _renderRoundedVerticalBar(x: number, y: number, width: number, height: number, radius: number, fill: string) {
  return (
    <path
      d={`M ${x + radius} ${y + height} L ${x + width - radius} ${y + height} Q ${x + width} ${y + height} ${x + width} ${y + height - radius} L ${x + width} ${y + radius} Q ${x + width} ${y} ${x + width - radius} ${y} L ${x + radius} ${y} Q ${x} ${y} ${x} ${y + radius} L ${x} ${y + height - radius} Q ${x} ${y + height} ${x + radius} ${y + height} Z`}
      fill={fill}
    />
  );
}

function _CustomVerticalBarShape(props: any, dividerColor: string) {
  const { x, y, width, height, payload } = props;
  const radius = 4;
  const value = payload?.signups ?? 0;

  if (value === 0) {
    const circleX = x + width / 2;
    const circleY = y - radius;
    return <circle cx={circleX} cy={circleY} r={radius} fill={dividerColor} />;
  }

  return _renderRoundedVerticalBar(x, y, width, height, radius, props.fill);
}

function _CustomVerticalBarShapeWide(props: any, dividerColor: string) {
  const { x, y, width, height, payload } = props;
  const radius = 5;
  const value = payload?.signups ?? 0;

  if (value === 0) {
    const rectWidth = width;
    const rectHeight = 8;
    const rectX = x + (width - rectWidth) / 2;
    const rectY = y - rectHeight;
    return (
      <rect x={rectX} y={rectY} width={rectWidth} height={rectHeight} rx={radius} ry={radius} fill={dividerColor} />
    );
  }

  return _renderRoundedVerticalBar(x, y, width, height, radius, props.fill);
}

function _CustomHorizontalBarShape(props: any, dividerColor: string) {
  const { x, y, width, height, payload } = props;
  const value = payload?.signups ?? 0;

  // Make the bar thinner (60% of original height)
  const barHeight = height * 0.6;
  const barY = y + (height - barHeight) / 2; // Center vertically

  if (value === 0) {
    // Render a grey circle for zero values
    const circleRadius = 4;
    const circleX = x + circleRadius;
    const circleY = y + height / 2;
    return <circle cx={circleX} cy={circleY} r={circleRadius} fill={dividerColor} />;
  }

  // Ensure minimum bar width of 5px
  const barWidth = Math.max(width, 5);

  // Render a horizontal bar for non-zero values with rounded corners
  const radius = 4;
  return (
    <path
      d={`M ${x} ${barY + radius} L ${x} ${barY + barHeight - radius} Q ${x} ${barY + barHeight} ${x + radius} ${barY + barHeight} L ${x + barWidth - radius} ${barY + barHeight} Q ${x + barWidth} ${barY + barHeight} ${x + barWidth} ${barY + barHeight - radius} L ${x + barWidth} ${barY + radius} Q ${x + barWidth} ${barY} ${x + barWidth - radius} ${barY} L ${x + radius} ${barY} Q ${x} ${barY} ${x} ${barY + radius} Z`}
      fill={props.fill}
    />
  );
}

function _CustomChartBase({ leftLabel, rightLabel }: { leftLabel: string; rightLabel: string }) {
  return (
    <Row flexDirection="column" alignItems="flex-end">
      <Column sx={{ mt: "2px", width: "calc(100% - 24px)" }}>
        <Divider sx={{ mt: "4px" }} />
        <Row sx={{ justifyContent: "space-between", mt: "12px" }}>
          <Typography variant="body2" sx={{ color: "text.secondary", fontSize: 12 }}>
            {leftLabel}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", fontSize: 12 }}>
            {rightLabel}
          </Typography>
        </Row>
      </Column>
    </Row>
  );
}

function _SummaryBox(props: {
  localizedPeriod: string;
  renderTopGap: boolean;
  signupsStats: StudentsSignupsStatsDto | null;
}) {
  const { localizedPeriod, renderTopGap, signupsStats } = props;
  const primaryColor = useThemeColor("primary");

  const trendIcon = signupsStats?.trend && _getTrendIcon(signupsStats.trend);
  const signupsText = signupsStats
    ? `${signupsStats.total} ${signupsStats.total === 1 ? "Anmeldung" : "Anmeldungen"}`
    : null;

  return (
    <Box
      sx={{
        mt: renderTopGap ? 5 : 0,
        p: 2,
        borderRadius: 2,
        bgcolor: "action.hover",
        display: "flex",
        alignItems: "center",
        gap: 2,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          bgcolor: primaryColor.main,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {trendIcon}
      </Box>
      <Box>
        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: 12 }}>
          {localizedPeriod}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: 16, whiteSpace: "nowrap" }}>
          {signupsText ?? <LinearProgress />}
        </Typography>
      </Box>
    </Box>
  );
}

function _getTrendIcon(trend: "up" | "down" | "stable") {
  const iconSx = { color: "primary.contrastText" };
  switch (trend) {
    case "up":
      return <ArrowOutwardIcon sx={iconSx} />;
    case "down":
      return <ArrowOutwardIcon sx={{ ...iconSx, transform: "rotate(90deg)" }} />;
    case "stable":
      return <ArrowForwardIcon sx={iconSx} />;
  }
}
